import React, { useRef, useEffect } from 'react';
import { Plus, MessageSquare, X, MoreHorizontal, Lock } from 'lucide-react';
import ConversationLengthIndicator from '../chat/ConversationLengthIndicator';

const ConversationPills = ({
  sessions,
  activeSessionId,
  onSelectSession,
  onNewSession,
  onArchiveSession,
  onOpenDrawer,
  messageCount,
}) => {
  const scrollRef = useRef(null);
  const activeRef = useRef(null);

  // Scroll vers le pill actif quand il change
  useEffect(() => {
    if (activeRef.current) {
      activeRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [activeSessionId]);

  const truncate = (str, max = 25) => {
    if (!str) return 'Sans titre';
    return str.length > max ? str.substring(0, max) + '…' : str;
  };

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '6px 8px',
      borderBottom: '1px solid var(--topbar-border)',
      minHeight: '40px',
      maxHeight: '40px',
    }}>
      {/* Bouton "+ Nouveau" toujours visible */}
      <button
        onClick={onNewSession}
        style={{
          display: 'flex', alignItems: 'center', gap: '4px',
          padding: '5px 10px', borderRadius: '20px',
          border: '1px solid var(--pill-soft-border)',
          background: 'var(--pill-soft-bg)',
          color: 'var(--pill-soft-text)',
          fontSize: '11px', fontWeight: '600',
          cursor: 'pointer', whiteSpace: 'nowrap',
          transition: 'all 0.2s',
          flexShrink: 0,
        }}
        title="Nouvelle discussion"
        onMouseEnter={e => {
          e.currentTarget.style.background = 'var(--pill-soft-bg-hover)';
          e.currentTarget.style.borderColor = 'var(--pill-soft-border-hover)';
          e.currentTarget.style.boxShadow = '0 2px 12px var(--pill-soft-glow)';
          e.currentTarget.style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.background = 'var(--pill-soft-bg)';
          e.currentTarget.style.borderColor = 'var(--pill-soft-border)';
          e.currentTarget.style.boxShadow = 'none';
          e.currentTarget.style.transform = 'translateY(0)';
        }}
      >
        <Plus size={12} /> Nouveau
      </button>

      {/* Pills scrollables */}
      <div
        ref={scrollRef}
        style={{
          display: 'flex',
          gap: '4px',
          flex: 1,
          overflowX: 'auto',
          scrollBehavior: 'smooth',
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}
        className="hide-scrollbar"
      >
        {sessions.map((session, idx) => {
          const isActive = session.id === activeSessionId;
          const isEphemeral = session.memory_scope === 'ephemeral';
          return (
            <div
              key={session.id}
              ref={isActive ? activeRef : null}
              title={isEphemeral ? 'Session hors mémoire (Suspendre la mémoire)' : undefined}
              style={{
                display: 'flex', alignItems: 'center', gap: '5px',
                padding: '4px 10px', borderRadius: '16px',
                background: isActive
                  ? (isEphemeral
                      ? 'linear-gradient(135deg, rgba(245,158,11,0.18), rgba(217,119,6,0.10))'
                      : 'var(--pill-soft-bg-hover)')
                  : (isEphemeral ? 'rgba(245,158,11,0.06)' : 'transparent'),
                border: isActive
                  ? (isEphemeral ? '1px solid rgba(245,158,11,0.4)' : '1px solid var(--pill-soft-border-hover)')
                  : (isEphemeral ? '1px solid rgba(245,158,11,0.2)' : '1px solid transparent'),
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'all 0.2s',
                flexShrink: 0,
                boxShadow: isActive ? (isEphemeral ? '0 0 8px rgba(245,158,11,0.15)' : '0 2px 10px var(--pill-soft-glow)') : 'none',
                animation: idx === 0 && sessions.length > 1 ? 'pillSlideIn 0.3s ease-out' : 'none',
              }}
              onClick={() => onSelectSession(session.id)}
              onMouseEnter={e => {
                if (!isActive) {
                  e.currentTarget.style.background = 'var(--conv-item-hover, rgba(255,255,255,0.05))';
                  e.currentTarget.style.border = '1px solid var(--card-border)';
                }
                // Montrer le bouton X
                const xBtn = e.currentTarget.querySelector('.pill-close');
                if (xBtn) xBtn.style.opacity = '1';
              }}
              onMouseLeave={e => {
                if (!isActive) {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.border = '1px solid transparent';
                }
                const xBtn = e.currentTarget.querySelector('.pill-close');
                if (xBtn) xBtn.style.opacity = '0';
              }}
            >
              {isEphemeral ? (
                <Lock
                  size={11}
                  style={{
                    color: isActive ? '#f59e0b' : 'rgba(245,158,11,0.75)',
                    flexShrink: 0,
                  }}
                />
              ) : (
                <MessageSquare
                  size={11}
                  style={{
                    color: isActive ? 'var(--pill-soft-icon)' : 'var(--text-muted)',
                    flexShrink: 0,
                  }}
                />
              )}
              <span style={{
                fontSize: '11px',
                fontWeight: isActive ? '600' : '400',
                color: isActive
                  ? (isEphemeral ? '#f59e0b' : 'var(--pill-soft-text)')
                  : (isEphemeral ? 'rgba(245,158,11,0.85)' : 'var(--text-secondary)'),
                maxWidth: '140px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                {truncate(session.title)}
              </span>
              {/* Bouton archiver discret — pas sur les sessions vides */}
              {onArchiveSession && (session.messages_count > 0) && (
                <span
                  className="pill-close"
                  onClick={(e) => {
                    e.stopPropagation();
                    onArchiveSession(session.id);
                  }}
                  style={{
                    opacity: 0,
                    display: 'flex', alignItems: 'center',
                    padding: '1px', borderRadius: '3px',
                    color: 'var(--text-muted)',
                    cursor: 'pointer',
                    transition: 'opacity 0.15s, color 0.15s',
                    flexShrink: 0,
                  }}
                  onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
                  onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
                  title="Archiver"
                >
                  <X size={10} />
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Badge longueur conversation compact */}
      <ConversationLengthIndicator
        messageCount={messageCount}
        onNewSession={onNewSession}
        type="project"
        compact={true}
      />

      {/* Bouton "···" pour ouvrir le drawer */}
      <button
        onClick={onOpenDrawer}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: '28px', height: '28px', borderRadius: '50%',
          border: '1px solid var(--card-border)',
          background: 'transparent',
          color: 'var(--text-secondary)',
          cursor: 'pointer',
          transition: 'all 0.2s',
          flexShrink: 0,
        }}
        onMouseEnter={e => {
          e.currentTarget.style.background = 'var(--accent-light)';
          e.currentTarget.style.color = 'var(--accent)';
          e.currentTarget.style.borderColor = 'var(--accent)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.color = 'var(--text-secondary)';
          e.currentTarget.style.borderColor = 'var(--card-border)';
        }}
        title="Gérer les conversations"
      >
        <MoreHorizontal size={14} />
      </button>

      {/* CSS pour masquer le scrollbar + animation */}
      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        @keyframes pillSlideIn {
          from { opacity: 0; transform: translateX(-12px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};

export default ConversationPills;
